import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Lock, ShieldCheck, EyeOff, ArrowUpRight, Video, Radio, Loader2, X, Plus, ArrowLeft } from "lucide-react";
import { Crest } from "../components/Media";
import { Avatar } from "../components/Media";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CoachTips } from "../components/CoachTip";

import { PROTON_AFFILIATE_URL } from "../lib/proton";
import { RecordingSection } from "../components/RecordingPlaybook";

const meetElapsed = (iso, now) => {
  const mins = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  const label = mins < 1 ? "just started" : mins < 60 ? `started ${mins}m ago` : `running ${Math.floor(mins / 60)}h ${mins % 60}m`;
  return { label, stale: mins >= 55 };
};

const WHY = [
  { icon: EyeOff, title: "No one listens in", body: "Audio, video and chat are encrypted on your device. Not even Proton can see or hear your call." },
  { icon: ShieldCheck, title: "No Big Tech, no algorithms", body: "The same principle as Sovereign Signal. Your conversations stay yours, end to end." },
  { icon: Lock, title: "Open & auditable", body: "Proton Meet is open-source and built on the MLS encryption standard." },
];

export default function EncryptedMeetUp() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const fromHearth = /^[a-f0-9]{24}$/i.test(params.get("from") || "") ? params.get("from") : null;
  const [rooms, setRooms] = useState(null);
  const [showHost, setShowHost] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try { const { data } = await api.get("/meet/rooms"); setRooms(data); } catch { setRooms([]); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const goLive = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/meet/rooms", { url: url.trim(), title: title.trim() });
      setUrl(""); setTitle(""); setShowHost(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not go live. Please check your link and try again.");
    }
    setBusy(false);
  };

  const endRoom = async (id) => {
    setRooms((rs) => (rs || []).filter((r) => r.id !== id));
    try { await api.delete(`/meet/rooms/${id}`); } catch { load(); }
  };

  const join = (u) => window.open(u, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen px-5 md:px-10 py-12 md:py-16" data-testid="encrypted-meetup-page">
      <div className="max-w-2xl mx-auto">
        {fromHearth && <Link to={`/hearth/${fromHearth}`} data-testid="meetup-back-to-hearth" className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white font-body text-sm mb-6"><ArrowLeft size={16} /> Back to the Hearth</Link>}
        <CoachTips screen="meetup" />
        {/* Joint-venture mark */}
        <div className="flex items-center gap-4 mb-10" data-testid="meetup-jv-mark">
          <Crest className="h-11 w-11 shrink-0" />
          <span className="shrink-0 h-6 w-6 rounded-full border border-white/15 flex items-center justify-center text-zinc-400 font-heading text-sm leading-none select-none" title="in collaboration with">&times;</span>
          <div className="relative shrink-0 h-11 w-11 border border-[#14A89A]/40 flex items-center justify-center bg-[#14A89A]/5">
            <Video strokeWidth={1.5} size={20} className="text-[#14A89A]" />
            <span className="absolute -bottom-1 -right-1 bg-[#0A0A0A] border border-[#14A89A]/40 rounded-full p-0.5">
              <Lock size={9} className="text-[#14A89A]" />
            </span>
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-body text-[10px] tracking-[0.3em] uppercase text-zinc-500">Sovereign Signal &amp; Proton</div>
            <div className="font-heading text-sm text-white">A private meet-up, in the works</div>
          </div>
        </div>

        <p className="font-body text-[10px] tracking-[0.3em] uppercase text-[#14A89A] mb-4">Powered by Proton Meet</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-white mb-6">Encrypted Meet&nbsp;Up</h1>

        <p className="font-body text-base text-zinc-300 leading-relaxed mb-5">
          Sovereign Signal meet-ups run on <span className="text-white">Proton Meet</span>, our private meet-up space:
          end-to-end encrypted video where not even Proton can see or hear your call. It runs on the same principle as
          SovSig, so your conversations stay yours.
        </p>
        <p className="font-body text-sm text-zinc-400 leading-relaxed mb-8">
          Anyone can <span className="text-zinc-200">join</span> a call with just a link. To <span className="text-zinc-200">host</span> one,
          you'll want a free Proton account.
        </p>

        {/* Proton free account specs */}
        <div className="border border-white/10 bg-[#121212] p-5 mb-10" data-testid="meetup-free-specs">
          <p className="font-body text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-4">What a free Proton account gets you</p>
          <ul className="space-y-3">
            <li className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] shrink-0 translate-y-1.5" />
              <span className="font-body text-sm text-zinc-300 leading-relaxed">Up to <span className="text-white">50 people</span> on a single call</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] shrink-0 translate-y-1.5" />
              <span className="font-body text-sm text-zinc-300 leading-relaxed"><span className="text-white">60 minutes</span> per meeting</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] shrink-0 translate-y-1.5" />
              <span className="font-body text-sm text-zinc-300 leading-relaxed"><span className="text-white">One</span> live meeting at a time</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] shrink-0 translate-y-1.5" />
              <span className="font-body text-sm text-zinc-300 leading-relaxed">Guests join with just a link, no account needed. Only the host needs a free Proton account.</span>
            </li>
          </ul>
          <p className="font-body text-xs text-zinc-500 leading-relaxed mt-4">
            Need longer calls, more people or recording? Those live on Proton's paid plans, which stretch to 100 people and far longer sessions.
          </p>
        </div>

        {/* How this stays private */}
        <div className="border-l-2 border-[#14A89A]/40 pl-4 mb-10" data-testid="meetup-privacy-note">
          <p className="font-body text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">How this stays private</p>
          <p className="font-body text-sm text-zinc-400 leading-relaxed">
            Every meet-up happens <span className="text-zinc-200">inside Proton</span>, encrypted end to end, so Sovereign Signal never
            touches your call. Nothing is recorded to SovSig and nothing is databased on our side. When the call ends, the conversation is
            gone, lost in time. The only thing SovSig ever holds is the <span className="text-zinc-200">join link</span> while a room is live,
            and the moment the host ends it, even that disappears.
          </p>
          <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">
            Want to keep a session? Proton lets the <span className="text-zinc-200">host</span> record on a paid Proton plan,
            and any recording lives in <span className="text-zinc-200">Proton, not SovSig</span>. As we grow, important meet ups can be
            recorded and stored in the <span className="text-zinc-200">Living Library</span> (our future database).
          </p>
        </div>

        {/* Live now + Host a meet */}
        <div className="border border-[#14A89A]/25 bg-[#14A89A]/5 p-5 mb-10" data-testid="host-meet-section">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <Radio strokeWidth={1.5} size={18} className="text-[#14A89A] shrink-0" />
              <span className="font-heading text-base text-white min-w-0">Live gatherings</span>
            </div>
            {!showHost && (
              <button onClick={() => { setShowHost(true); setErr(""); }} data-testid="host-meet-open" className="shrink-0 flex items-center gap-1.5 border border-[#14A89A]/40 text-[#14A89A] px-3 py-2 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
                <Plus size={14} /> Host a meet
              </button>
            )}
          </div>

          {showHost && (
            <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-4 space-y-3" data-testid="host-meet-form">
              <p className="font-body text-xs text-zinc-400 leading-relaxed">
                Start a room in <span className="text-zinc-200">Proton Meet</span>, then paste its join link here. Everyone will see a
                "Live now" button. SovSig only stores this link, never your call. End it anytime and the link is gone.
              </p>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's it about? (optional)" data-testid="host-meet-title" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body" />
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://meet.proton.me/…" data-testid="host-meet-url" className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body" />
              {err && <p className="font-body text-xs text-red-400" data-testid="host-meet-error">{err}</p>}
              <div className="flex gap-2">
                <button onClick={goLive} disabled={busy || !url.trim()} data-testid="host-meet-golive" className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 font-body text-sm hover:bg-[#2BC4B5] transition-colors disabled:opacity-40">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Radio size={15} />} Go live
                </button>
                <button onClick={() => { setShowHost(false); setErr(""); }} data-testid="host-meet-cancel" className="border border-white/15 text-zinc-300 px-4 py-2.5 font-body text-sm hover:bg-white/5 transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {rooms === null ? (
            <div className="flex justify-center py-4"><Loader2 className="animate-spin text-zinc-500" size={18} /></div>
          ) : rooms.length === 0 ? (
            <p className="font-body text-xs text-zinc-500" data-testid="no-live-meets">No live gatherings right now. Start one above.</p>
          ) : (
            <div className="space-y-2" data-testid="live-meets-list">
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-white/10 px-4 py-3" data-testid={`live-meet-${r.id}`}>
                  <Avatar name={r.host_name} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] animate-pulse" />
                      <span className="font-body text-[10px] uppercase tracking-widest text-[#14A89A]">Live now</span>
                      {(() => { const e = meetElapsed(r.created_at, now); return (
                        <span data-testid={`meet-elapsed-${r.id}`} className={`font-body text-[10px] ${e.stale ? "text-amber-500" : "text-zinc-500"}`}>
                          &middot; {e.label}{e.stale ? " (free calls cap at 60m)" : ""}
                        </span>
                      ); })()}
                    </div>
                    <div className="font-heading text-sm text-white truncate mt-0.5">{r.title || `${r.host_name}'s room`}</div>
                    <div className="font-body text-xs text-zinc-500 truncate">Hosted by {r.host_name}</div>
                  </div>
                  <button onClick={() => join(r.url)} data-testid={`join-meet-${r.id}`} className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-2 font-body text-xs hover:bg-[#2BC4B5] transition-colors shrink-0">
                    Join <ArrowUpRight size={14} />
                  </button>
                  {(r.mine || user?.role === "admin") && (
                    <button onClick={() => endRoom(r.id)} data-testid={`end-meet-${r.id}`} title="End room" className="shrink-0 text-zinc-600 hover:text-red-400 p-1"><X size={16} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Why Proton */}
        <RecordingSection />
        <div className="border border-white/10 bg-[#121212] divide-y divide-white/5 mb-10" data-testid="why-proton">
          <div className="px-5 py-3 font-body text-[10px] tracking-[0.25em] uppercase text-zinc-500">Why Proton</div>
          {WHY.map((w) => (
            <div key={w.title} className="flex items-start gap-4 px-5 py-4">
              <w.icon strokeWidth={1.5} size={18} className="text-[#14A89A] mt-0.5 shrink-0" />
              <div>
                <div className="font-heading text-sm text-white">{w.title}</div>
                <div className="font-body text-xs text-zinc-400 leading-relaxed mt-1">{w.body}</div>
              </div>
            </div>
          ))}
        </div>

        <a
          href={PROTON_AFFILIATE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="proton-affiliate-cta"
          className="inline-flex items-center justify-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-7 py-4 hover:bg-[#2BC4B5] transition-colors font-body text-sm w-full sm:w-auto"
        >
          Create your Proton account <ArrowUpRight size={16} />
        </a>

        <p className="font-body text-xs text-zinc-600 leading-relaxed mt-6 max-w-lg">
          The more sovereigns who privatize with Proton, the more of our circle can gather face to face, privately.
        </p>
      </div>
    </div>
  );
}
